import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ForecastConfig, ForecastResult } from '../types/wasm';

interface ModelState {
  // Model configuration
  config: ForecastConfig;

  // Forecast results
  result: ForecastResult | null;

  // Model execution state
  isRunning: boolean;
  executionTime: number | null;
  error: string | null;

  // ARIMAX support
  isMultivariateModel: boolean;
  exogenousVariables: string[];

  // Checkpoint state for Agent Mode
  checkpointData: any | null;
  isAwaitingCheckpoint: boolean;

  // Actions
  setConfig: (config: Partial<ForecastConfig>) => void;
  updateModelParams: (params: Partial<ForecastConfig['model_params']>) => void;
  setResult: (result: ForecastResult | null) => void;
  setIsRunning: (isRunning: boolean) => void;
  setExecutionTime: (time: number | null) => void;
  setError: (error: string | null) => void;
  setMultivariateModel: (isMultivariate: boolean) => void;
  setExogenousVariables: (variables: string[]) => void;
  setCheckpointData: (data: any | null) => void;
  setAwaitingCheckpoint: (awaiting: boolean) => void;
  resetModel: () => void;
}

const defaultConfig: ForecastConfig = {
  model_type: 'arima',
  horizon: 12,
  confidence_level: 0.95,
  preprocessing: {
    missing_value_method: 'linear',
    handle_outliers: false,
    outlier_method: 'iqr',
    iqr_multiplier: 1.5,
    transformation: 'none',
    differencing_order: 0,
  },
  model_params: {
    p: 1,
    d: 0,
    q: 1,
  },
};

export const useModelStore = create<ModelState>()(
  devtools(
    set => ({
      config: defaultConfig,
      result: null,
      isRunning: false,
      executionTime: null,
      error: null,
      isMultivariateModel: false,
      exogenousVariables: [],
      checkpointData: null,
      isAwaitingCheckpoint: false,

      setConfig: config => {
        console.log('[ModelStore] setConfig called:', {
          incoming: config,
          current: useModelStore.getState().config
        });

        if ('horizon' in config) {
          const nextHorizon = config.horizon;
          if (
            typeof nextHorizon !== 'number' ||
            !Number.isFinite(nextHorizon) ||
            nextHorizon <= 0
          ) {
            console.error('[ModelStore] ERROR: Attempted to set invalid horizon:', nextHorizon);
            console.error('[ModelStore] Blocking invalid horizon value. Using fallback: 12');
            config = { ...config, horizon: 12 };
          }
        }

        set(state => {
          const newConfig = { ...state.config, ...config };
          console.log('[ModelStore] Config updated:', {
            old_horizon: state.config.horizon,
            new_horizon: newConfig.horizon,
            full_new_config: newConfig
          });

          // Final validation
          if (
            typeof newConfig.horizon !== 'number' ||
            !Number.isFinite(newConfig.horizon) ||
            newConfig.horizon <= 0
          ) {
            console.error('[ModelStore] CRITICAL: Config horizon is still invalid after merge!', newConfig);
          }

          return { config: newConfig };
        });
      },

      updateModelParams: params =>
        set(state => ({
          config: {
            ...state.config,
            model_params: { ...state.config.model_params, ...params },
          },
        })),

      setResult: result => set({ result }),

      setIsRunning: isRunning => set({ isRunning }),

      setExecutionTime: executionTime => set({ executionTime }),

      setError: error => set({ error }),

      setMultivariateModel: isMultivariate => set({ isMultivariateModel: isMultivariate }),

      setExogenousVariables: variables => set({ exogenousVariables: variables }),

      setCheckpointData: data => set({ checkpointData: data }),

      setAwaitingCheckpoint: awaiting => set({ isAwaitingCheckpoint: awaiting }),

      resetModel: () =>
        set({
          config: defaultConfig,
          result: null,
          isRunning: false,
          executionTime: null,
          error: null,
          isMultivariateModel: false,
          exogenousVariables: [],
          checkpointData: null,
          isAwaitingCheckpoint: false,
        }),
    }),
    { name: 'ModelStore' }
  )
);
